import {
  DatabaseAuto, IbulkDelete,
  IbulkDeleteSuccessResponse,
  IcommerceUser,
  IdataArrayResponse,
  IdeleteOneResponse,
  IfilterProps,
  InonOperatingRevenue,
  InonOperatingRevenueAnalytics,
  TnonOperatingRevenueRequest,
  validateData
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../commerce-client';

type TnonOperatingRevenueProps = InonOperatingRevenue<number, string, string, IcommerceUser>;

/**
 * Represents a non-operating revenue record in the system.
 * This class provides methods to manage and track revenue that is not part of
 * the company's primary operations, such as investment income, asset sales, etc.
 *
 * @extends DatabaseAuto - Provides common database operation methods
 * @template TnonOperatingRevenueProps - The type definition for non-operating revenue properties
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link NonOperatingRevenue} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const nonOperatingRevenue = await NonOperatingRevenue.getAll();
 *
 * const nonOperatingRevenue = await NonOperatingRevenue.filterAll({
 *
 * });
 *
 * const nonOperatingRevenue = await NonOperatingRevenue.bulkDelete({
 *
 * });
 *
 * const nonOperatingRevenue = await NonOperatingRevenue.getOne('resourceId');
 *
 * const nonOperatingRevenue = await NonOperatingRevenue.remove();
 *
 * ```
 */
export class NonOperatingRevenue extends DatabaseAuto {
  /** Base API endpoint for non-operating revenue operations */
  private static readonly baseUrl = '/non-operating-revenue';

  /** Unique reference ID for the revenue record */
  readonly urId: TnonOperatingRevenueProps['urId'];

  /** Company associated with the revenue */
  readonly company: TnonOperatingRevenueProps['company'];

  /** Name or description of the revenue source */
  readonly name: TnonOperatingRevenueProps['name'];

  /** User who recorded the revenue */
  readonly commerceUser: TnonOperatingRevenueProps['commerceUser'];

  /** Amount of the revenue */
  readonly amount: TnonOperatingRevenueProps['amount'];

  /** Additional notes about the revenue */
  readonly note: string;

  /**
   * Creates a new instance of NonOperatingRevenue.
   * @param {TnonOperatingRevenueProps} data - The revenue data used to initialize the instance
   * @throws {Error} If the data is invalid or missing required fields
   */
  constructor(data: TnonOperatingRevenueProps) {
    validateData(data, {
      urId: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'Unique reference ID must be a non-empty string'
      },
      name: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'NonOperatingRevenue name must be a non-empty string'
      },
      commerceUser: {
        validate: (v) =>
          typeof v === 'object' &&
          v._id.trim().length > 0,
        message: 'Added by name must be a valid user object'
      },
      amount: {
        validate: (v) =>
          typeof v === 'number' &&
          v >= 0,
        message: 'Amount must be a non-negative number'
      },
      company: {
        required: false,
        validate: (v) =>
          v === undefined ||
          (typeof v === 'string' && v.trim().length > 0),
        message: 'Company ID must be a non-empty string if provided'
      }
    });

    super(data);

    this.urId = data.urId;
    this.company = data.company;
    this.name = data.name;
    this.commerceUser = data.commerceUser;
    this.amount = data.amount;
    this.note = data.note || '';
  }

  /**
   * Retrieves analytics for non-operating revenue based on the provided filter criteria.
   * @param {IfilterProps} filter - The filter criteria for analytics
   * @returns {Promise<InonOperatingRevenueAnalytics>} The analytics results
   * @throws {Error} If there's an error fetching the analytics
   */
  static getNonOperatingRevenueAnalytics(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<InonOperatingRevenueAnalytics>(`${NonOperatingRevenue.baseUrl}/analytics`, filter);

    return lastValueFrom(observer$);
  }

  /**
   * Retrieves all non-operating revenue records with pagination support.
   * @param {number} [offset=0] - The starting index for pagination
   * @param {number} [limit=20] - The maximum number of records to return
   * @returns {Promise<{ count: number; dataArr: NonOperatingRevenue[] }>}
   * An object containing the total count and an array of revenue records
   * @throws {Error} If there's an error fetching the records
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();
    const observer$ = CommerceClient.ehttp
      .makeGet<IdataArrayResponse<TnonOperatingRevenueProps>>(`${NonOperatingRevenue.baseUrl}?${query}`);
    const nonOperatingRevenues = await lastValueFrom(observer$);

    return {
      count: nonOperatingRevenues.count,
      dataArr: nonOperatingRevenues.data.map((val) => new NonOperatingRevenue(val))
    };
  }

  /**
   * Filters non-operating revenue records based on specified criteria.
   * @param {IfilterProps} filter - The filter criteria to apply
   * @returns {Promise<{ count: number; dataArr: NonOperatingRevenue[] }>}
   * An object containing the total count and an array of filtered revenue records
   * @throws {Error} If there's an error applying the filter
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<TnonOperatingRevenueProps>>(`${NonOperatingRevenue.baseUrl}/filter`, filter);
    const nonOperatingRevenues = await lastValueFrom(observer$);

    return {
      count: nonOperatingRevenues.count,
      dataArr: nonOperatingRevenues.data.map((val) => new NonOperatingRevenue(val))
    };
  }

  /**
   * Retrieves a single non-operating revenue record by its ID.
   * @param {string} resourceId - The unique identifier of the record to retrieve
   * @returns {Promise<NonOperatingRevenue>} The requested revenue record
   * @throws {Error} If the record is not found or an error occurs during retrieval
   */
  static async getOne(resourceId: string) {
    const observer$ = CommerceClient.ehttp
      .makeGet<TnonOperatingRevenueProps>(`${NonOperatingRevenue.baseUrl}/${resourceId}`);
    const nonOperatingRevenue = await lastValueFrom(observer$);

    return new NonOperatingRevenue(nonOperatingRevenue);
  }

  /**
   * Creates a new non-operating revenue record.
   * @param {TnonOperatingRevenueRequest<true>} payload - The revenue data to create
   * @returns {Promise<NonOperatingRevenue>} The newly created revenue record
   * @throws {Error} If the creation fails
   */
  static async add(payload: TnonOperatingRevenueRequest<true>) {
    const observer$ = CommerceClient.ehttp
      .makePost<TnonOperatingRevenueProps>(`${NonOperatingRevenue.baseUrl}`, payload);

    return new NonOperatingRevenue(await lastValueFrom(observer$));
  }

  /**
   * Deletes multiple non-operating revenue records based on the provided criteria.
   * @param {IbulkDelete} payload - The criteria for identifying which records to delete
   * @returns {Promise<IbulkDeleteSuccessResponse>} The result of the bulk delete operation
   * @throws {Error} If the bulk delete operation fails
   */
  static bulkDelete(payload: IbulkDelete) {
    const observer$ = CommerceClient.ehttp
      .makePost<IbulkDeleteSuccessResponse>(`${NonOperatingRevenue.baseUrl}/bulk-delete`, payload);

    return lastValueFrom(observer$);
  }

  /**
   * Updates the current non-operating revenue record with new values.
   * @param {TnonOperatingRevenueRequest} payload - The data to update the record with
   * @returns {Promise<NonOperatingRevenue>} The updated revenue record
   * @throws {Error} If the update operation fails
   */
  async update(payload: TnonOperatingRevenueRequest) {
    const observer$ = CommerceClient.ehttp
      .makePut<TnonOperatingRevenueProps>(`${NonOperatingRevenue.baseUrl}/${this._id}`, payload);

    return new NonOperatingRevenue(await lastValueFrom(observer$));
  }

  /**
   * Removes the current non-operating revenue record from the system.
   * @returns {Promise<IdeleteOneResponse | null>} The result of the delete operation
   * @throws {Error} If the deletion fails or the record doesn't exist
   */
  remove() {
    const observer$ = CommerceClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${NonOperatingRevenue.baseUrl}/${this._id}`);

    return lastValueFrom(observer$);
  }
}
